import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import utils from '../../utils';
import { HiTrash } from 'react-icons/hi';
import adminServices from '../../services/AdminServices';
import FormError from '../feedback/FormError';
import { useAdminData } from '../../context/SessionContext';

function CountyModal({ action, activeCounty, onReload, setActiveCounty }) {
    const [name, setName] = useState('');
    const [loading, setLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [error, setError] = useState(null);
    const { setCounties } = useAdminData();

    const tabs = useMemo(() => {
        const baseTabs = ['Info'];
        if (action === 1) {
            return [...baseTabs, 'Delete'];
        }
        return baseTabs;
    }, [action]);

    const updateSession = () => {
        adminServices.getCounties().then(
            (res) => {
                setCounties(res.data || []);
            },
            (err) => {
                console.log('Session update error', err);
            }
        );
    };

    useEffect(() => {
        if (action === 1 && activeCounty) {
            setName(activeCounty.name || '');
        } else {
            setName('');
        }
    }, [action, activeCounty]);

    const handleSubmit = (e) => {
        e.preventDefault();
        setError(null);
        const closeBtn = document.getElementById('county-modal-close-btn');
        setLoading(true);

        const payload = { name };

        let request;
        if (action === 1) {
            request = adminServices.updateCounty(activeCounty.id, payload);
        } else {
            request = adminServices.addCounty(payload);
        }

        request
            .then((res) => {
                const successMsg = action === 1 ? 'County Updated Successfully' : 'County Added Successfully';
                toast.success(successMsg);
                setLoading(false);
                onReload?.();
                updateSession();
                closeBtn?.click();
            })
            .catch((err) => {
                toast.error(utils.toastError);
                console.error(err);
                setError(err);
                setLoading(false);
            });
    };

    const handleDelete = () => {
        const closeBtn = document.getElementById('county-modal-close-btn');
        setDeleteLoading(true);

        adminServices.deleteCounty(activeCounty.id)
            .then((res) => {
                toast.success('County Deleted Successfully');
                setDeleteLoading(false);
                onReload?.();
                updateSession();
                closeBtn?.click();
            })
            .catch((err) => {
                toast.error(utils.toastError);
                console.error(err);
                setDeleteLoading(false);
            });
    };

    const changeTab = (idx) => {
        if (action === 1) {
            setActiveTab(idx);
        }
    };

    const [activeTab, setActiveTab] = useState(0);

    function clearData() {
        setName('');
        setActiveTab(0);
        setActiveCounty(null);
        setError(null);
    }

    return (
        <div>
            <label htmlFor="county-modal" id="county-open-btn" className="hidden">
                Add / Update County
            </label>
            <input type="checkbox" id="county-modal" className="modal-toggle" />

            <div className="modal">
                <div className="modal-box w-8/11 max-w-2xl">
                    <label
                        onClick={clearData}
                        htmlFor="county-modal"
                        id="county-modal-close-btn"
                        className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
                    >
                        ✕
                    </label>

                    <h2 className="text-lg font-bold mb-3">
                        {action === 1 ? 'Edit' : 'Add'}
                        <span> County{action === 1 && activeCounty && ':'}</span>
                        {action === 1 && activeCounty && (
                            <span className='text-gray-500 ml-2'>{activeCounty?.name}</span>
                        )}
                    </h2>

                    <div className="flex justify-center mb-6">
                        <div className="border-gray-200 dark:border-border-dark">
                            <div className="tabs tabs-boxed justify-center">
                                {tabs.map((tab, idx) => (
                                    // eslint-disable-next-line jsx-a11y/anchor-is-valid
                                    <a
                                        key={idx}
                                        className={`tab ${activeTab === idx
                                            ? 'tab-active bg-primary text-white rounded-lg'
                                            : ''
                                            }`}
                                        onClick={() => changeTab(idx)}
                                        role="tab"
                                        aria-selected={activeTab === idx}
                                    >
                                        {tab}
                                    </a>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="">
                        {action === 1 && activeCounty && (
                            <>
                                <div className="border border-border-light dark:border-border-dark bg-surface-light dark:bg-surface-dark p-3 shadow-sm transition-all hover:shadow-md rounded-xl mb-6">
                                    <div className="space-y-2 text-sm">
                                        <p className='font-bold'>Overview</p>
                                        <p>
                                            <span className="font-bold mr-2">Patients Count:</span>
                                            {activeCounty.patient_count}
                                        </p>
                                        <p>
                                            <span className="font-bold mr-2">Nurse Count:</span>
                                            {activeCounty.nurse_count}
                                        </p>
                                        {activeCounty.creation_time && (
                                            <p>
                                                <span className="font-bold mr-2">Created:</span>
                                                {new Date(activeCounty.creation_time).toLocaleDateString()}
                                            </p>
                                        )}
                                    </div>
                                </div>

                            </>
                        )}
                    </div>

                    {activeTab === 0 && (
                        <form onSubmit={handleSubmit}>
                            {error && <FormError error={error} />}

                            <div className="form-control mb-3">
                                <label className="label">
                                    <span className="label-text">Name *</span>
                                </label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    required
                                    className="input input-bordered w-full"
                                    placeholder="e.g. Nairobi"
                                />
                            </div>

                            <div className="mt-6">
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-sky-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-75"
                                >
                                    {loading ? (
                                        <span>{action === 1 ? 'Updating...' : 'Adding...'}</span>
                                    ) : (
                                        <span>{action === 1 ? 'Update' : 'Add'}</span>
                                    )}
                                </button>
                            </div>
                        </form>
                    )}

                    {activeTab === 1 && (
                        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-border-light dark:border-border-dark overflow-hidden mt-6">
                            <div className="p-6 md:p-8">
                                <div className="border-l-4 border-error pl-4 py-2">
                                    <h3 className="font-bold text-error">Danger Zone</h3>
                                    <p className="text-sm text-gray-600 dark:text-gray-500 mt-2">
                                        Deleting this county is irreversible.
                                    </p>
                                    <div className="pt-4">
                                        {deleteLoading ? (
                                            <div className="flex justify-center py-2">
                                                <span className="loading loading-spinner loading-sm"></span>
                                            </div>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={handleDelete}
                                                className="flex items-center space-x-2 bg-error text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-error/90 transition-colors"
                                            >
                                                <HiTrash className="h-4 w-4" />
                                                <span>Delete: {activeCounty?.name}</span>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default CountyModal;